// Romanian-locale Excel splits CSV on the list separator ';' (',' is the
// decimal mark there); the BOM makes Excel read the file as UTF-8.
export const CSV_SEPARATOR = ';';
const BOM = '﻿';

const escapeCsv = (value) => {
  const s = value == null ? '' : String(value);
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (rows) =>
  BOM + rows.map((r) => r.map(escapeCsv).join(CSV_SEPARATOR)).join('\r\n');

const THOUSANDS_GROUPS = /^\d{1,3}(?:x\d{3})+$/;

const isGrouped = (intPart, sep) =>
  THOUSANDS_GROUPS.test(intPart.split(sep).join('x'));

/**
 * Number shown by a cell, or null when the text is not a number or is
 * ambiguous ("1.234" is 1234 in ro-RO and 1.234 in en-GB) — those cells stay
 * text so the export never shows a value different from the screen.
 *
 * @returns {{ value: number, percent: boolean } | null}
 */
export const parseDisplayedNumber = (text) => {
  let s = String(text ?? '').replace(/[\s  ]/g, '');
  const percent = s.endsWith('%');
  if (percent) s = s.slice(0, -1);
  const match = /^([-+]?)([\d.,]+)$/.exec(s);
  if (!match) return null;
  const [, sign, body] = match;
  if (/^0\d/.test(body) || body.replace(/\D/g, '').length > 15) return null;

  const lastDot = body.lastIndexOf('.');
  const lastComma = body.lastIndexOf(',');
  let intPart = body;
  let fraction = '';
  if (lastDot >= 0 && lastComma >= 0) {
    const decimal = lastDot > lastComma ? '.' : ',';
    const thousands = decimal === '.' ? ',' : '.';
    const at = body.lastIndexOf(decimal);
    intPart = body.slice(0, at);
    fraction = body.slice(at + 1);
    if (!/^\d+$/.test(fraction) || !isGrouped(intPart, thousands)) return null;
    intPart = intPart.split(thousands).join('');
  } else if (lastDot >= 0 || lastComma >= 0) {
    const sep = lastDot >= 0 ? '.' : ',';
    const parts = body.split(sep);
    if (parts.length > 2) {
      if (!isGrouped(body, sep)) return null;
      intPart = parts.join('');
    } else {
      const [head, tail] = parts;
      if (!head || !tail) return null;
      if (tail.length === 3 && head.length <= 3) return null;
      intPart = head;
      fraction = tail;
    }
  }
  const value = Number(`${sign}${intPart}${fraction ? `.${fraction}` : ''}`);
  if (!Number.isFinite(value)) return null;
  return { value: percent ? value / 100 : value, percent };
};

const SHEET_NAME_MAX = 31;

/** Excel sheet name: no []:*?/\ , at most 31 chars, unique within `used`. */
export const sheetName = (title, index, used) => {
  const base =
    String(title || '')
      .replace(/[[\]:*?/\\]/g, ' ')
      .replace(/^'+|'+$/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, SHEET_NAME_MAX) || `Sheet ${index + 1}`;
  let name = base;
  for (let n = 2; used.has(name.toLowerCase()); n += 1) {
    const suffix = ` (${n})`;
    name = base.slice(0, SHEET_NAME_MAX - suffix.length) + suffix;
  }
  used.add(name.toLowerCase());
  return name;
};

/** File-system-safe name that keeps letters with diacritics. */
export const fileSlug = (text) =>
  String(text || '')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 80) || 'export';
